import React, { useEffect, useRef, useState } from "react";
import { useCart } from "../context/CartContext";
import { money, api } from "../lib/api";
import { Link, useNavigate } from "react-router-dom";
import { Reveal } from "../components/Reveal";
import { toast } from "sonner";
import { CheckCircle2, AlertTriangle, Lock } from "lucide-react";
import { STATE_TAX_RATES, US_STATES } from "../lib/taxRates";

function loadSquareSdk(env) {
  return new Promise((resolve, reject) => {
    if (window.Square) return resolve(window.Square);
    const src = env === "production"
      ? "https://web.squarecdn.com/v1/square.js"
      : "https://sandbox.web.squarecdn.com/v1/square.js";
    const s = document.createElement("script");
    s.src = src;
    s.onload = () => resolve(window.Square);
    s.onerror = () => reject(new Error("Failed to load Square SDK"));
    document.head.appendChild(s);
  });
}

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const [form, setForm] = useState({ full_name: "", email: "", address: "", city: "", state: "", zip_code: "" });
  const [order, setOrder] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [config, setConfig] = useState(null);
  const [agree, setAgree] = useState(false);
  const cardRef = useRef(null);
  const containerRef = useRef(null);
  const navigate = useNavigate();

  const shipping = subtotal > 0 ? 49 : 0;
  const tax = subtotal * (STATE_TAX_RATES[(form.state || "").toUpperCase().trim()] || 0);
  const total = subtotal + shipping + tax;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    api.get("/config/square").then((r) => setConfig(r.data)).catch(() => setConfig({ configured: false }));
  }, []);

  const trackCart = () => {
    const email = (form.email || "").trim();
    if (!email || !email.includes("@") || items.length === 0) return;
    api.post("/cart/track", {
      email,
      items: items.map((i) => ({ product_id: i.product_id, name: i.name, price: i.price, quantity: i.quantity, image: i.image })),
    }).catch(() => {});
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!config?.configured || !config.application_id || cardRef.current) return;
      try {
        const Square = await loadSquareSdk(config.env);
        if (cancelled) return;
        const payments = Square.payments(config.application_id, config.location_id);
        const card = await payments.card();
        await card.attach(containerRef.current);
        cardRef.current = card;
      } catch (e) {
        console.error(e);
      }
    })();
    return () => { cancelled = true; };
  }, [config]);

  const submit = async (e) => {
    e.preventDefault();
    if (!agree) { toast.error("Please agree to the terms to continue."); return; }
    setSubmitting(true);
    try {
      let source_id = null;
      if (config?.configured) {
        if (!cardRef.current) { toast.error("Payment form not ready yet."); setSubmitting(false); return; }
        const result = await cardRef.current.tokenize();
        if (result.status !== "OK") {
          toast.error("Card could not be verified. Check the details and try again.");
          setSubmitting(false);
          return;
        }
        source_id = result.token;
      }
      const res = await api.post("/checkout", {
        items: items.map((i) => ({ product_id: i.product_id, name: i.name, price: i.price, quantity: i.quantity, image: i.image })),
        ...form, source_id,
      });
      setOrder(res.data);
      clear();
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Checkout failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (order) {
    return (
      <div className="pt-40 pb-40">
        <div className="max-w-xl mx-auto px-6 text-center" data-testid="order-confirmation">
          <CheckCircle2 size={56} className="mx-auto text-[#00f0ff]" />
          <h1 className="font-display text-5xl uppercase mt-6">Order <span className="bttf-text">Confirmed</span></h1>
          {order.payment_status === "UNCONFIGURED" && (
            <p className="text-amber-400 text-sm mt-3">Payment processing is not yet connected — this order is recorded as pending payment.</p>
          )}
          <div className="mt-8 border border-[#27272a] p-6 text-left space-y-2">
            <div className="flex justify-between"><span className="text-zinc-400">Order #</span><span className="font-mono text-[#00f0ff]" data-testid="order-number">{order.order_number}</span></div>
            <div className="flex justify-between"><span className="text-zinc-400">Total</span><span className="font-bold">{money(order.total)}</span></div>
            <div className="flex justify-between"><span className="text-zinc-400">Status</span><span className="uppercase text-emerald-400">{order.status}</span></div>
          </div>
          <div className="flex gap-4 justify-center mt-8">
            <Link to="/account" className="border border-[#00f0ff] text-[#00f0ff] font-bold uppercase tracking-wider px-6 py-3">View Orders</Link>
            <Link to="/" className="bttf-gradient text-black font-bold uppercase tracking-wider px-6 py-3">Home</Link>
          </div>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="pt-40 pb-40 text-center">
        <h1 className="font-display text-5xl uppercase">Cart Empty</h1>
        <button onClick={() => navigate("/category/rims")} className="mt-6 bttf-gradient text-black font-bold uppercase tracking-wider px-8 py-4">Start Shopping</button>
      </div>
    );
  }

  return (
    <div className="pt-28 pb-24">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal><h1 className="font-display text-5xl md:text-7xl uppercase leading-none mb-4">Checkout</h1></Reveal>

        {config && !config.configured && (
          <div className="flex items-start gap-3 border border-amber-500/40 bg-amber-500/10 p-4 mb-8 text-sm text-amber-200" data-testid="payment-notice">
            <AlertTriangle size={18} className="mt-0.5 shrink-0" />
            <span>Live card processing (Square) is not connected yet. You can place an order now and it will be recorded as <b>pending payment</b> until Square keys are added.</span>
          </div>
        )}

        <form onSubmit={submit} className="grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 space-y-5">
            <div className="font-display text-2xl uppercase mb-2">Shipping Details</div>
            {[
              { k: "full_name", l: "Full Name", type: "text" },
              { k: "email", l: "Email", type: "email" },
              { k: "address", l: "Street Address", type: "text" },
            ].map((f) => (
              <div key={f.k}>
                <label className="block text-[10px] uppercase tracking-[0.2em] text-[#00f0ff] mb-1">{f.l}</label>
                <input required type={f.type} value={form[f.k]} onChange={set(f.k)} onBlur={f.k === "email" ? trackCart : undefined} data-testid={`checkout-${f.k}`} className="w-full bg-[#111] border-b-2 border-[#27272a] focus:border-[#00f0ff] py-3 px-3 outline-none transition-colors" />
              </div>
            ))}
            <div className="grid grid-cols-3 gap-5">
              <div>
                <label className="block text-[10px] uppercase tracking-[0.2em] text-[#00f0ff] mb-1">City</label>
                <input required value={form.city} onChange={set("city")} data-testid="checkout-city" className="w-full bg-[#111] border-b-2 border-[#27272a] focus:border-[#00f0ff] py-3 px-3 outline-none transition-colors" />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.2em] text-[#00f0ff] mb-1">State</label>
                <select required value={form.state} onChange={set("state")} data-testid="checkout-state" className="w-full bg-[#111] border-b-2 border-[#27272a] focus:border-[#00f0ff] py-3 px-2 outline-none transition-colors">
                  <option value="">—</option>
                  {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.2em] text-[#00f0ff] mb-1">Zip</label>
                <input required value={form.zip_code} onChange={set("zip_code")} data-testid="checkout-zip" className="w-full bg-[#111] border-b-2 border-[#27272a] focus:border-[#00f0ff] py-3 px-3 outline-none transition-colors" />
              </div>
            </div>

            {config?.configured && (
              <div>
                <div className="font-display text-2xl uppercase mb-2 mt-8 flex items-center gap-2"><Lock size={18} className="text-[#00f0ff]" /> Payment</div>
                <div ref={containerRef} data-testid="square-card" className="border border-[#27272a] bg-[#111] p-4 min-h-[56px]" />
                <p className="text-xs text-zinc-500 mt-2">Secured by Square. Card details never touch our servers.</p>
              </div>
            )}
          </div>

          <div>
            <div className="border border-[#27272a] p-6 sticky top-24">
              <div className="font-display text-2xl uppercase mb-4">Order Summary</div>
              <div className="space-y-3 max-h-64 overflow-y-auto mb-4">
                {items.map((i) => (
                  <div key={i.product_id} className="flex gap-3 items-center">
                    <img src={i.image} alt="" className="h-12 w-12 object-cover border border-[#27272a]" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm truncate">{i.name}</div>
                      <div className="text-xs text-zinc-500">Qty {i.quantity}</div>
                    </div>
                    <div className="text-sm font-semibold">{money(i.price * i.quantity)}</div>
                  </div>
                ))}
              </div>
              <div className="border-t border-[#27272a] pt-4 space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-zinc-400">Subtotal</span><span>{money(subtotal)}</span></div>
                <div className="flex justify-between"><span className="text-zinc-400">Shipping</span><span>{money(shipping)}</span></div>
                <div className="flex justify-between"><span className="text-zinc-400">Tax (est.)</span><span>{money(tax)}</span></div>
                <div className="flex justify-between text-lg font-bold pt-2 border-t border-[#27272a]"><span>Total</span><span data-testid="checkout-total">{money(total)}</span></div>
              </div>
              <label className="flex items-start gap-2 mt-5 cursor-pointer" data-testid="agree-terms">
                <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-0.5 accent-[#00f0ff]" />
                <span className="text-xs text-zinc-400">I agree to the <Link to="/policies" target="_blank" className="text-[#00f0ff] hover:underline">shipping, returns &amp; warranty terms</Link>.</span>
              </label>
              <button type="submit" disabled={submitting} data-testid="place-order" className="w-full mt-4 bttf-gradient text-black font-bold uppercase tracking-wider py-4 disabled:opacity-50 hover:opacity-90 transition-opacity">
                {submitting ? "Processing..." : config?.configured ? "Pay & Place Order" : "Place Order"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
